import {
  CACHE_REVALIDATE_5_MIN_MS,
  NEXT_JS_SERVER_COMPONENT_DEFAULT_CACHING_STRATEGY,
} from "@nimble/react-requests/api-clients/caching-strategy";
import { useQuery } from "@tanstack/react-query";
import { create, keyResolver, windowScheduler } from "@yornaath/batshit";

import {
  batchSchoolData,
  BatchSchoolDataResponse,
} from "@/lib/api-clients/candidate-api-client";

type SchoolDataItem = BatchSchoolDataResponse["data"][number];

const resolverKey: keyof SchoolDataItem = "nces_id";

export class SchoolNotFoundError extends Error {
  constructor(ncesId: string) {
    super(`Failed to bulk fetch school data for ${ncesId}`);
    this.name = "SchoolNotFoundError";
  }
}

const batchedFetcher = create({
  fetcher: async (ids: string[]) => {
    const { data } = await batchSchoolData(
      NEXT_JS_SERVER_COMPONENT_DEFAULT_CACHING_STRATEGY,
    )(ids);

    return data;
  },
  resolver: keyResolver(resolverKey),
  scheduler: windowScheduler(5),
});

export default function useBatchedSchoolData({ ncesId }: { ncesId: string }) {
  return useQuery({
    queryKey: [`batched-school-data-${ncesId}`],
    queryFn: async () => {
      const schoolData = await batchedFetcher.fetch(ncesId);

      if (!schoolData) {
        throw new SchoolNotFoundError(ncesId);
      }

      return schoolData;
    },
    staleTime: CACHE_REVALIDATE_5_MIN_MS,
  });
}
